import { ChainInfo, ConnectedWallet, KeplrController, StationController, UnsignedTx, WalletController, WalletName, WalletType } from "@goblinhunt/cosmes/wallet";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { CLASSIC_CHAIN_ID, CLASSIC_DENOM, CLASSIC_GAS_PRICE, RPC_URL, WALLET_SETTINGS, WC_PROJECT_ID } from "utils/Constants";
import { showLoadingToast } from "utils/Toasts";

type _Wallet = {
    address?: string,
    post: (tx: UnsignedTx, additionalFee?: number) => Promise<string>,
    connect: (name: WalletName, type: WalletType) => Promise<void>,
    disconnect: () => void
}

const WalletContext = createContext<_Wallet>({
    connect: async (_: WalletName, __: WalletType) => { },
    disconnect: () => { },
    post: async () => { return "" }
})

export const useWallet = () => useContext(WalletContext)

export function WalletProvider({ children }: { children: ReactNode }) {
    const [controller, setController] = useState<WalletController>()
    const [wallet, setWallet] = useState<ConnectedWallet>()

    const post = async (tx: UnsignedTx, additionalFee: number = 0) => {
        let fee = await wallet!.estimateFee(tx, 1.5)
        fee.amount = fee.amount.map(e => {
            if (e.denom == "uluna") e.amount = (Number(e.amount) + Math.trunc(additionalFee)).toString()
            return e;
        })
        return await wallet!.broadcastTx(tx, fee)
    }

    const connect = async (name: WalletName, type: WalletType) => {
        try {
            const controller = WALLET_SETTINGS[name].controller
            const chainInfo: ChainInfo<string> = {
                chainId: CLASSIC_CHAIN_ID,
                gasPrice: {
                    amount: CLASSIC_GAS_PRICE,
                    denom: CLASSIC_DENOM
                },
                rpc: RPC_URL
            }
            const result = await controller.connect(type, [chainInfo])
            setWallet(result.get(CLASSIC_CHAIN_ID))
            setController(controller)
        } catch (e) {
            showLoadingToast("Connect Wallet", async () => { throw e; })
        }
    }

    const disconnect = () => {
        controller?.disconnect([CLASSIC_CHAIN_ID])
        setWallet(undefined)
        setController(undefined)
    }

    useEffect(() => {
        for (const controller of Object.values(WALLET_SETTINGS).map(e => e.controller)) {
            controller.onDisconnect(_ => disconnect())
        }
    }, [])

    return <WalletContext.Provider value={{
        connect,
        disconnect,
        address: wallet?.address,
        post
    }}>
        {children}
    </WalletContext.Provider>
}